/**
 * 自動下書き (S5)。
 *
 * ユーザー入力は「物件名・住所」等の物件固有情報のみ。
 * 避難先 (避難場所/避難所) は **公式マスター (evacuationData) から自動選定** する
 * (本プロジェクトの原則: 避難先は公式オープンデータから選ぶ。自由入力の経路を作らない)。
 * 経路は Directions 実徒歩経路 + ポリゴン境界切り詰め (fetchAccurateRoute)。
 */
import { screenExtent, screenPointToLngLat } from '@/lib/geo/rotated-frame';
import { maxSiteAreaM2 } from '@/lib/evacuation/site-size';
import { ringAreaM2 } from '@/lib/evacuation-sites/open-space';
import { geocodeAddress } from '@/services/geocoding';
import {
  fetchWalkingRoute,
  fetchWalkingRoutes,
  fetchWalkingRouteThrough,
  type WalkingRouteResult,
} from '@/services/directions';
import {
  fetchAreaPolygonByLandmarks,
  fetchFacilityFootprint,
  fetchWalkableBridgesNearSegment,
  type BridgeCrossing,
} from '@/services/overpass';
import {
  fetchMapboxFootprint,
  fetchMapboxBuilding,
  fetchMapboxBridges,
  fetchMapboxBoundaryEntries,
  fetchMapboxNearbyRoads,
  fetchMapboxRoadCenterlines,
} from '@/services/mapbox-footprint';
import { fetchGsiBuilding } from '@/services/gsi-footprint';
import { cleanPlaceLabel } from '@/lib/evacuation/place-label';
import {
  loadEvacuationPlaces,
  type EvacuationPlaceExtended,
} from '@/services/evacuationData';
import { loadAssignmentData } from '@/services/evacuationAssignment';
import {
  boundaryPointsAround,
  extendRouteIntoPolygon,
  minDistanceToRouteMeters,
  nearestPointOnPolygonBoundary,
  orderByProgress,
  perpendicularViaPoints,
  pointInsideRing,
  polygonCentroid,
  removeShallowBends,
  resolveRouteEndpoint,
  resolveRouteStart,
  routeTotalTurnDeg,
  simplifyRouteCoords,
} from '@/lib/geo/polygon-utils';
import { haversineMeters } from '@/lib/geo/distance';
import { fetchMaxFloodDepth } from '@/services/floodDepth';
import { resolveBousaiPortalUrl } from './bousai-portal';
import { lookupAssignment } from './official-assignment';
import {
  placeCoord,
  selectOfficialDestinations,
  prefectureOfAddress,
  municipalityOfAddress,
  type PickOptions,
  type PickResult,
  type PickDetail,
} from './pick-destinations';
import { resolveCardLabel } from './card-label';

/**
 * 避難先の代表点と実形状の許容ズレ (m)。これを超えたら形状が別物と判断して捨てる。
 * 実測: テスト用の敷地形状は代表点の45m外 (門が代表点のケース相当) で正常、
 * 文化総合センター大和田で拾われた別建物は83m外。その間に置く。
 */
const DESTINATION_SHAPE_MAX_GAP_M = 60;
import {
  ROUTE_SIMPLIFY_TOLERANCE_M,
  DESTINATION_PENETRATION_M,
} from '@/lib/geo/route-tuning';
// 経路の後処理は v1 / v2 / bake スクリプトで同じ実装を使う (合成の重複が
// 「bake から trim が抜ける」不具合を生んだため、合成ごと共有する)。
import {
  destinationRing,
  entranceGate,
  nearestRing,
  postprocessRoute,
  splitEntranceWaypoint,
} from '@/lib/geo/route-postprocess';
import { holesWithin, trimRouteAtHoles } from '@/lib/geo/polygon-holes';
import type {
  DraftPropertyV2,
  DestinationV2,
  LngLat,
  MapState,
  PropertyKind,
  PropertyV2,
} from '@/types/property-v2';
import { ES_LAYOUT } from './tokens';

// 経路チューニング定数は v1 / v2 / bake スクリプトで共有する (route-tuning.ts)。
// ここからも re-export して既存の import 元を壊さない。
export { ROUTE_SIMPLIFY_TOLERANCE_M, DESTINATION_PENETRATION_M };
// 住所の解析は pick-destinations.ts へ移した。既存の import 元
// (EvacuationTab.vue・PropertyNew.vue) を壊さないよう re-export する。
export { prefectureOfAddress, municipalityOfAddress } from './pick-destinations';
export type { PickDetail } from './pick-destinations';

/** ポリゴン取得の既定検索半径 (m)。 */
export const POLYGON_SEARCH_RADIUS = 800;

/**
 * 物件自身の建物フットプリント検索半径 (m)。喫煙所マップ (SmokingEditor) と同値。
 * fetchFacilityFootprint の既定 90 より絞ってあるのは、周囲の別建物を掴む確率を
 * 下げるため (内包判定があるので誤採用はしないが、クエリを軽くする効果もある)。
 */
export const PROPERTY_FOOTPRINT_RADIUS_M = 60;

/**
 * 避難先の範囲ポリゴンを OSM(Overpass) から取得する。
 * names が複数なら union/convex で1つに合体。失敗時は OverpassError を投げる。
 */
export async function fetchDestinationPolygon(
  center: LngLat,
  names: string[],
  radiusMeters = POLYGON_SEARCH_RADIUS,
): Promise<Array<[number, number]>> {
  return fetchAreaPolygonByLandmarks({
    center,
    radiusMeters,
    landmarkNames: names.filter((n) => n.trim().length > 0),
    combineMode: 'union',
  });
}

/**
 * 避難先の「正確な範囲リング」を可能な限り取得する。取れなければ null。
 *   1. Mapbox タイル (敷地 landuse > 建物。Overpass に依らず即時・確実)
 *   2. GSI 基盤地図情報の建物 (内包 > 近傍30m。Mapbox/OSM に無い建物の受け皿)
 *   3. 名称ベースの union (名前が明確な公園/学校/キャンパスに強い)
 *   4. 座標を内包する施設フットプリント (名称非依存: 表記ゆれ施設に有効)
 * どの段も障害/不一致で例外を出しうるので個別に握りつぶし、次段へ。
 * null の場合、呼び出し側で簡易円フォールバックを当てる (範囲は必ず保持する)。
 */
export async function resolveDestinationPolygon(
  coords: LngLat,
  names: string[],
): Promise<Array<[number, number]> | null> {
  // 公園の中の学校などが公園全体を拾わないよう、名前の種類で敷地の大きさを制限する
  // (lib/evacuation/site-size.ts)。名前の付いた union (3) は名前で選ぶので制限しない。
  const maxAreaM2 = maxSiteAreaM2(names[0] ?? '');
  try {
    const ring = await fetchMapboxFootprint(coords, { maxGroundsAreaM2: maxAreaM2 });
    if (ring && ring.length >= 3) return ring;
  } catch {
    /* プローブ障害 → GSI へ */
  }
  try {
    const ring = await fetchGsiBuilding(coords);
    if (ring && ring.length >= 3) return ring;
  } catch {
    /* プローブ障害 → OSM 名称へ */
  }
  try {
    const ring = await fetchDestinationPolygon(coords, names);
    if (ring.length >= 3) return ring;
  } catch {
    /* 名称不一致・Overpass障害 → フットプリントへ */
  }
  try {
    const fp = await fetchFacilityFootprint(coords);
    // 名前に依らない内包なので、大きすぎる施設 (公園全体など) は採らない。
    if (fp && fp.length >= 3 && ringAreaM2(fp) <= maxAreaM2) return fp;
  } catch {
    /* Overpass障害 → 呼び出し側の円フォールバックへ */
  }
  return null;
}

/** 取込時にユーザーが与える情報 (物件固有のみ)。避難先はマスターから自動選定。 */
export interface OfficialDraftInput {
  buildingName: { ja: string; en?: string };
  address: string;
  /**
   * 任意: 呼び出し側が既に geocode 済みの座標。
   * 指定時は address の再 geocode を行わない (直列チェーンの先頭を1往復ぶん短縮)。
   */
  coords?: LngLat;
  qrUrl?: string;
  /** 避難所カードの浸水深 (例 "0.5m")。省略時は非表示。 */
  floodDepth?: string;
  /** 任意: 避難場所を名前で指定 (省略時は公式マスターから自動選定)。 */
  evacuationName?: string;
  /** 任意: 避難所を名前で指定 (省略時は公式マスターから自動選定)。 */
  shelterName?: string;
  /** 任意: 物件種別 (R2)。ヒント・テンプレ提案に効く。 */
  propertyType?: PropertyKind;
  /** 物件マスタ (HubSpot) との紐付け。マスタから選んだ場合のみ入る。 */
  hubspotPropertyId?: string;
}

/**
 * 東京都防災ホームページ。
 *
 * かつては全物件の QR 既定値だったが、避難先を指定するのは区市町村なので、
 * 既定は住所から自治体の防災ポータルを引く (resolveBousaiPortalUrl) ように変えた。
 * この定数は「都のページ」を明示的に指したい場合の値として残している。
 */
export const DEFAULT_QR_URL = 'https://www.bousai.metro.tokyo.lg.jp/';

export interface AccurateRouteResult {
  coordinates: Array<[number, number]>;
  /** Directions API の徒歩距離 (m, 四捨五入)。 */
  distance: number;
  /**
   * 組み合わせラベル (例 "A1" = 物件面A × 進入点1)。A,B,C…=物件側の道路面
   * (A=primary)、1,2,…=避難先の進入点 (1=最寄り)。組み合わせ候補のみ持つ。
   */
  label?: string;
}

/**
 * 旧アプリ (トップページの避難マップ) の経路機能と同等の「正確な徒歩経路」を取得する。
 *   1. 終点解決: polygonがあれば 最寄りエリアの OSM入口 優先 → 境界最近接点 (resolveRouteEndpoint)
 *   2. Mapbox Directions (walking) で実経路を取得
 *   3. 経路が polygon 内部へ貫通した分を境界で切り詰め (trimRouteAtPolygonBoundary)
 * 矢印の描画スタイル (ArrowLayer) はそのままで、パス形状だけが実経路になる。
 * Directions 失敗時は throw (呼び出し側で直線フォールバック)。
 */
/**
 * 経路の始点(物件入口)・終点(避難先入口)を解決する。
 * 終点 = 避難先ポリゴンの OSM 入口(既存 resolveRouteEndpoint)。
 * 始点 = 物件 footprint の入口(目的地方向, resolveRouteStart) ?? 物件座標。
 * どちらも Overpass 失敗に非致命 (実経路は継続)。
 */
async function resolveRouteEndpoints(
  property: LngLat,
  destCoords: LngLat,
  destRing: Array<[number, number]> | null,
  footprint?: DestinationV2['polygon'],
  entrances = true,
  /** ユーザーが経由点で指定した入口 (splitEntranceWaypoint)。あれば自動の進入点は探さない。 */
  fixedEndpoint: LngLat | null = null,
): Promise<{
  /**
   * 経路始点の候補群 (物件の道路面ごとの縁。OSM 入口が取れた場合はそれが先頭)。
   * 呼び出し側が各面から経路を引き「経路が最も短くなる面」を primary に採用する。
   */
  starts: LngLat[];
  endpoint: LngLat;
  fpRing: Array<[number, number]> | null;
  /** 避難先境界の歩行可能な進入点 (物件に近い順)。端点バリエーションの材料。 */
  destEntries: LngLat[];
}> {
  let endpoint = fixedEndpoint ?? destCoords;
  let destEntries: LngLat[] = [];
  if (destRing) {
    let source = 'coords';
    if (!fixedEndpoint) {
      try {
        const resolved = await resolveRouteEndpoint(property, destRing, {
          entrances,
        });
        if (resolved) {
          endpoint = resolved.coords;
          source = resolved.source;
        }
      } catch {
        endpoint = destCoords;
      }
    }
    // Mapbox road タイルの進入点 (歩ける道×境界の交差・行き止まり・街路沿いサンプル)
    // は **常に** 取得する (教訓45)。以前は OSM 入口が取れた時にスキップしており、
    // Overpass の生死で候補数が激減するランダムな挙動になっていた (阪本小学校で実測)。
    // OSM 入口が取れた場合はそれを primary 終点として優先し、進入点群は候補に使う。
    // null = プローブ故障 (破棄済み) — 新品のプローブで1回だけリトライする。
    let fetched = await fetchMapboxBoundaryEntries(destRing);
    if (fetched === null) fetched = await fetchMapboxBoundaryEntries(destRing);
    const entries = fetched ?? [];
    if (entries.length > 0) {
      destEntries = [...entries].sort(
        (a, b) => haversineMeters(property, a) - haversineMeters(property, b),
      );
      // 入口を指定したときは終点を変えない (進入点は entranceGate の材料)。
      if (!fixedEndpoint && source !== 'entrance') endpoint = destEntries[0]!;
    }
  }
  let starts: LngLat[] = [property];
  const fpRing = footprint ? nearestRing(property, footprint) : null;
  if (fpRing) {
    // 物件座標(中心)を始点にすると Directions が中心を不適切な道にスナップし、経路が
    // 塗り (fill) を貫通し精度も落ちる (実測: グラントウキョウ)。始点候補は
    // 「物件近傍の各道路面に面した footprint 境界点」— 物理的に一番近い面ではなく、
    // 呼び出し側が **経路が一番近くなる面** を採用する (実測: プライムアーバンは南側の
    // path が物理的には最近だが、正しい出発は北側の街路)。他の面は経路候補として提示。
    const faces: LngLat[] = [];
    const push = (p: LngLat | null | undefined) => {
      if (!p) return;
      if (!faces.some((f) => haversineMeters(f, p) < 8)) faces.push(p);
    };
    // OSM 入口 (entrance) が取れればそれを先頭候補に
    try {
      push(await resolveRouteStart(fpRing, endpoint));
    } catch {
      /* OSM障害は非致命 */
    }
    // null = プローブ故障 (破棄済み) — 新品のプローブで1回だけリトライする。
    // 輪郭を渡して端の面も拾う (細長い建物では中心からの探索で端が落ちる)。
    let fetchedRoads = await fetchMapboxNearbyRoads(property, fpRing);
    if (fetchedRoads === null) {
      fetchedRoads = await fetchMapboxNearbyRoads(property, fpRing);
    }
    const roadPts = fetchedRoads ?? [];
    for (const r of roadPts) {
      // footprint 内部を通る道 (中庭通路等) は「出口の面」ではないので除外。
      // 上位枠を食って四方の街路 (例: 西側の直進道路) を追い出すのを防ぐ。
      if (pointInsideRing(r, fpRing)) continue;
      push(nearestPointOnPolygonBoundary(fpRing, r));
    }
    // 道路面が1つも取れない時のフォールバック: 目的地方向の縁
    if (faces.length === 0) {
      push(nearestPointOnPolygonBoundary(fpRing, endpoint) ?? property);
    }
    if (faces.length > 0) starts = faces;
  }
  return { starts, endpoint, fpRing, destEntries };
}

/**
 * 直線距離に対しこの倍率を超える遠回りの経路のみ「橋認識」を発動する。
 * 直線に近い普通の経路では Overpass 橋クエリを打たない (バッチ時のコスト/負荷を抑制)。
 */
const BRIDGE_DETOUR_TRIGGER = 1.25;

/**
 * start→end 近隣の橋を経由した徒歩ルート群を取得する。
 *
 * Mapbox の徒歩ルート探索は川等を渡る際に最短でない橋を選ぶことがある
 * (経由地を1つ足すだけで短くなる = 無指定ルートが最適でない)。そこで直線に近い橋を
 * OSM から列挙し各橋経由の実ルートを作る。ただし「直線からの遠回り率」が閾値未満の
 * 経路 (=障害物を跨いでいない直線的な経路) では発動しない。橋取得失敗は非致命 ([])。
 */
/** 橋認識を発動すべきか (直線に対する遠回り率がしきい値超)。 */
function bridgeGateFires(
  start: LngLat,
  endpoint: LngLat,
  naturalDistance: number,
): boolean {
  const straight = haversineMeters(start, endpoint);
  return straight >= 1 && naturalDistance > straight * BRIDGE_DETOUR_TRIGGER;
}

/**
 * start→end 近隣の「歩いて渡れる橋」を列挙する。
 * Mapbox road タイル (安定・数百ms) を主にし、プローブ不可 (null) のときだけ
 * Overpass に落とす。Overpass のみ依存だと間欠障害時に橋経由の最短候補が
 * 黙って消える (実測: プライムアーバンの新亀島橋 650m。教訓44)。
 */
async function listWalkableBridges(
  start: LngLat,
  endpoint: LngLat,
): Promise<BridgeCrossing[]> {
  const bridges = await fetchMapboxBridges(start, endpoint);
  if (bridges !== null) return bridges;
  try {
    return await fetchWalkableBridgesNearSegment(start, endpoint);
  } catch {
    return []; // Overpass も障害 → 橋認識なし
  }
}

async function resolveBridgeRoutes(
  start: LngLat,
  endpoint: LngLat,
  naturalDistance: number,
): Promise<{ routes: WalkingRouteResult[]; crossing: LngLat | null }> {
  if (!bridgeGateFires(start, endpoint, naturalDistance)) {
    return { routes: [], crossing: null };
  }
  const bridges = await listWalkableBridges(start, endpoint);
  if (bridges.length === 0) return { routes: [], crossing: null };
  const settled = await Promise.allSettled(
    bridges.map((br) =>
      fetchWalkingRouteThrough([start, { lng: br.lng, lat: br.lat }, endpoint]),
    ),
  );
  const routes: WalkingRouteResult[] = [];
  // 「最短の橋」= 経由で自然経路より短くなる橋。端点/道路面バリエーションの経由地に使う。
  let crossing: LngLat | null = null;
  let best = naturalDistance;
  settled.forEach((s, i) => {
    if (s.status !== 'fulfilled') return;
    routes.push(s.value);
    if (s.value.distance < best) {
      best = s.value.distance;
      const br = bridges[i]!;
      crossing = { lng: br.lng, lat: br.lat };
    }
  });
  return { routes, crossing };
}

export interface AccurateRouteOptions {
  /**
   * 橋認識 (Overpass 1回 + Directions 最大4回の追加往復) を行うか。既定 true。
   *
   * 新規作成フローでは false にして待ち時間を削る。橋経由の最適化は編集ページの
   * 「経路候補」(fetchAccurateRouteCandidates) で必要なときに取り直せる。
   */
  bridges?: boolean;
  /**
   * 避難先の OSM 入口(門)を探すか (Overpass 1往復)。既定 true。
   *
   * polygon が実形状ではなく暫定円のときは false にする。円の境界上の「入口」は
   * 実在の敷地入口ではないため、往復ぶんが無駄になる。
   */
  entrances?: boolean;
}

export async function fetchAccurateRoute(
  property: LngLat,
  destCoords: LngLat,
  polygon?: DestinationV2['polygon'],
  footprint?: DestinationV2['polygon'],
  opts: AccurateRouteOptions = {},
  waypoints: LngLat[] = [],
): Promise<AccurateRouteResult> {
  const ring = destinationRing(property, destCoords, polygon);
  // 避難先の内側に置いた最後の経由点は入口 = 終点。残りが「通過する点」。
  const { entrance, vias: viaWaypoints } = splitEntranceWaypoint(waypoints, ring);
  const destHoles = ring ? holesWithin(ring, polygon?.holes) : [];
  const { starts, endpoint, fpRing, destEntries } = await resolveRouteEndpoints(
    property,
    destCoords,
    ring,
    footprint,
    opts.entrances !== false,
    entrance,
  );
  // 経路回廊の車道中心線 (歩道スナップ先)。Directions と並行に取得しておく。
  const roadLinesPromise = fetchRoadCenterlinesWithRetry([
    property,
    ...starts,
    endpoint,
    ...(entrance ? destEntries.slice(0, 3) : []),
  ]);
  let best = await routeToEndpoint(endpoint);
  // 入口の経由点は、入口に一番近い道から入る (範囲の中の通路を歩いて届く経路を避ける)。
  const gate =
    entrance && ring ? entranceGate(best.coordinates, ring, entrance, destEntries) : null;
  if (gate) {
    try {
      best = await routeToEndpoint(gate);
    } catch {
      /* 進入点への経路が取れなければ入口への経路のまま */
    }
  }
  const roadLines = (await roadLinesPromise) ?? [];
  return {
    coordinates: postprocessRoute(best.coordinates, ring, fpRing, {
      roadLines,
      destHoles,
      entrance,
    }),
    distance: Math.round(best.distance),
  };

  async function routeToEndpoint(endpoint: LngLat): Promise<WalkingRouteResult> {
    // 各道路面から自然経路を引き、暫定の最短面を得る (橋補正前)。
    let start = starts[0] ?? property;
    let natural: WalkingRouteResult | null = null;
    // 経路の採否は「距離 + 折れペナルティ」— 数m短いだけの折れ経路より直進を選ぶ。
    // 折れ角は平滑化後の形で測る (生ジオメトリの歩道ノイズで順位が壊れるため)。
    const scoreOf = (r: WalkingRouteResult) =>
      r.distance + smoothedTurnDeg(r.coordinates) * TURN_PENALTY_M_PER_DEG;
    if (starts.length > 1) {
      const settled = await Promise.allSettled(
        starts.map((s) => {
          const wp = buildComboWaypoints(s, endpoint, null, viaWaypoints);
          return wp.length > 2
            ? fetchWalkingRouteThrough(wp)
            : fetchWalkingRoute(s, endpoint);
        }),
      );
      settled.forEach((r, i) => {
        if (r.status !== 'fulfilled') return;
        if (!natural || scoreOf(r.value) < scoreOf(natural)) {
          natural = r.value;
          start = starts[i]!;
        }
      });
    }
    if (!natural) {
      const wp = buildComboWaypoints(start, endpoint, null, viaWaypoints);
      natural =
        wp.length > 2
          ? await fetchWalkingRouteThrough(wp)
          : await fetchWalkingRoute(start, endpoint);
    }
    // 遠回りなら橋経由も試し、最良を採る (Mapbox徒歩の最適でない橋選択を補正)。
    // さらに「最短の橋」経由で **各道路面** を引き直し、経路が最良になる面を採用する
    // (自然経路だと正しい橋を渡らない面が長く見え、誤った面が選ばれる — ユーザー方針)。
    let best: WalkingRouteResult = natural;
    if (opts.bridges !== false) {
      const { routes: bridgeRoutes, crossing } = await resolveBridgeRoutes(
        start,
        endpoint,
        natural.distance,
      );
      if (viaWaypoints.length === 0) {
        for (const r of bridgeRoutes) {
          if (scoreOf(r) < scoreOf(best)) best = r;
        }
      }
      if (crossing && starts.length > 1) {
        const faceSettled = await Promise.allSettled(
          starts.map((s) =>
            fetchWalkingRouteThrough(
              buildComboWaypoints(s, endpoint, crossing, viaWaypoints),
            ),
          ),
        );
        for (const r of faceSettled) {
          if (r.status === 'fulfilled' && scoreOf(r.value) < scoreOf(best)) {
            best = r.value;
          }
        }
      }
    }
    return best;
  }
}

/**
 * 候補として提示する最大本数。物件面×進入点の全組み合わせ候補 (どの面・どの入口が
 * 正しいかはユーザーにしか特定できない) に合わせ拡大。距離昇順で採用するため、
 * 上限が小さいと裏側 (遠い) の組が近い組に押し出されて消える。幾何デデュープが
 * 同一経路の組を畳むので、実表示は通常これより大幅に少ない。
 */
const MAX_ROUTE_CANDIDATES = 30;
/**
 * 組み合わせ候補 (面×入口) の Directions 同時実行数。全組み合わせは最大 100 近くに
 * なり得るため、無制限の並列はレート制限 (429) で黙って歯抜けになる。
 */
const COMBO_CONCURRENCY = 8;

/** 並列数を制限した map (個別失敗は null)。 */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<Array<R | null>> {
  const out: Array<R | null> = new Array(items.length).fill(null) as Array<
    R | null
  >;
  let next = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (next < items.length) {
        const i = next++;
        try {
          out[i] = await fn(items[i]!);
        } catch {
          out[i] = null;
        }
      }
    },
  );
  await Promise.all(workers);
  return out;
}
/**
 * 終点バリアントに使う進入点の最大数 (最近の1点は primary 終点として使用済み)。
 * Directions 呼び出し数の上限 = コストの上限。検出される進入点は 15m デデュープ後で
 * 高々十数点なので、実質「全進入点」をカバーする値にしてある。
 */
const MAX_ENTRY_VARIANTS = 24;
/** 最短候補に対しこの倍率を超える迂回は「不自然」として除外する。 */
const CANDIDATE_MAX_DETOUR_RATIO = 1.7;
/** 2経路を「同一」とみなすサンプル点間の許容距離 (m)。 */
const CANDIDATE_DEDUP_M = 35;
/** 候補が「最短橋を通っている」とみなす、経路と橋横断点の最大距離 (m)。 */
const BRIDGE_MATCH_M = 50;
/**
 * 順位付けの折れペナルティ (m/度)。90° の折れ ≈ +22.5m 相当。距離が拮抗する候補は
 * まっすぐな経路を上位/既定にする (距離表示自体は実値のまま)。
 */
const TURN_PENALTY_M_PER_DEG = 0.25;
/**
 * 順位付けの「道路外ラン」ペナルティ。可視道路から離れて連続する最長ラン (m) が
 * 許容 (40m = 建物出入り・広場リード・門ダイブ級) を超えた分に掛ける (m/m)。
 * 歩行者デッキ回廊など「見える道路が無い場所」を長く走る候補を沈める
 * (実測: 横浜ランドマークタワー — みなとみらいのデッキ経由 121m ランの候補が
 * 街路経由より上位に出た)。許容 40m 以下は無罰 = 検証済みサイトの序列は不変。
 */
const OFF_ROAD_RUN_ALLOWANCE_M = 40;
const OFF_ROAD_RUN_PENALTY_PER_M = 2;
/**
 * これを超える道路外ランの候補は (クリーンな候補が3本以上あるとき) 一覧から
 * 除外する。降格だけでは破線プレビューに残って「道路外を直進する候補」に見える。
 * 100m = 建物リード/門ダイブ級 (≤80m) は残し、デッキ回廊級 (121m〜) を落とす。
 */
const OFF_ROAD_RUN_DROP_M = 100;

/**
 * 可視道路から離れて連続する最長ラン (m)。5m 刻みでサンプルし、可視中心線から
 * 8m 以内・避難先リング内/際 (20m)・物件敷地内は「離れていない」扱い。
 */
function maxOffRoadRunM(
  coords: Array<[number, number]>,
  roadLines: Array<Array<[number, number]>>,
  destRing: Array<[number, number]> | null,
  fpRing: Array<[number, number]> | null,
): number {
  if (roadLines.length === 0 || coords.length < 2) return 0;
  const lat0 = coords[0]![1];
  const kx = Math.cos((lat0 * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const toM = (q: [number, number]): [number, number] => [q[0] * kx, q[1] * ky];
  const nearRoad = (pt: [number, number]): boolean => {
    const [px, py] = toM(pt);
    for (const line of roadLines) {
      for (let i = 1; i < line.length; i++) {
        const [ax, ay] = toM(line[i - 1]!);
        const [bx, by] = toM(line[i]!);
        const dx = bx - ax;
        const dy = by - ay;
        const l2 = dx * dx + dy * dy;
        if (l2 <= 0) continue;
        let t = ((px - ax) * dx + (py - ay) * dy) / l2;
        t = Math.max(0, Math.min(1, t));
        if (Math.hypot(px - (ax + t * dx), py - (ay + t * dy)) <= 8) return true;
      }
    }
    return false;
  };
  const nearRing = (pt: [number, number], r: Array<[number, number]>): boolean => {
    if (pointInsideRing({ lng: pt[0], lat: pt[1] }, r)) return true;
    const [px, py] = toM(pt);
    for (let i = 0; i < r.length; i++) {
      const [ax, ay] = toM(r[i]!);
      const [bx, by] = toM(r[(i + 1) % r.length]!);
      const dx = bx - ax;
      const dy = by - ay;
      const l2 = dx * dx + dy * dy;
      if (l2 <= 0) continue;
      let t = ((px - ax) * dx + (py - ay) * dy) / l2;
      t = Math.max(0, Math.min(1, t));
      if (Math.hypot(px - (ax + t * dx), py - (ay + t * dy)) <= 20) return true;
    }
    return false;
  };
  let run = 0;
  let maxRun = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = toM(coords[i - 1]!);
    const b = toM(coords[i]!);
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const n = Math.max(1, Math.ceil(L / 5));
    for (let k = 0; k <= n; k++) {
      const pt: [number, number] = [
        coords[i - 1]![0] + ((coords[i]![0] - coords[i - 1]![0]) * k) / n,
        coords[i - 1]![1] + ((coords[i]![1] - coords[i - 1]![1]) * k) / n,
      ];
      if (
        nearRoad(pt) ||
        (destRing && nearRing(pt, destRing)) ||
        (fpRing && pointInsideRing({ lng: pt[0], lat: pt[1] }, fpRing))
      ) {
        if (run > maxRun) maxRun = run;
        run = 0;
        continue;
      }
      run += L / n;
    }
  }
  return Math.max(maxRun, run);
}

/**
 * 経路回廊の車道中心線を取得する (1回リトライ付き)。
 * プローブの一時障害 (タイル未ロード・WebGL 再生成中) で null/空になると、スナップが
 * **黙って無効化**され「歩道の粗い経路」が実行によって混ざる — 非決定な見た目の悪化は
 * ユーザーには乱数に見える (教訓46 と同型)。1回のリトライでほぼ解消する。
 */
async function fetchRoadCenterlinesWithRetry(
  points: LngLat[],
): Promise<Array<Array<[number, number]>>> {
  const first = await fetchMapboxRoadCenterlines(points);
  if (first && first.length > 0) return first;
  const second = await fetchMapboxRoadCenterlines(points);
  return second ?? [];
}

/**
 * 折れペナルティ用の折れ角合計 (度)。**生の Directions ジオメトリで測ってはいけない** —
 * 分離歩道・横断歩道由来の微細な折れが実測 300° 超あり、0.25m/° を掛けると数十 m 級の
 * 順位ノイズになる (レビュー再現: 実質直線の 150m 経路が本物の 90° 折れの 166m 経路に
 * 負ける逆転)。簡略化 + 浅い折れ除去で描画相当の形に均してから測る。
 */
function smoothedTurnDeg(coords: Array<[number, number]>): number {
  return routeTotalTurnDeg(
    removeShallowBends(simplifyRouteCoords(coords, ROUTE_SIMPLIFY_TOLERANCE_M)),
  );
}

/** 経路を弧長比 fractions の位置でサンプリングする (dedup用)。 */
function sampleRouteAlong(
  coords: Array<[number, number]>,
  fractions: number[],
): LngLat[] {
  if (coords.length === 0) return [];
  if (coords.length === 1) {
    const p = { lng: coords[0]![0], lat: coords[0]![1] };
    return fractions.map(() => p);
  }
  const cum = [0];
  for (let i = 1; i < coords.length; i++) {
    cum.push(
      cum[i - 1]! +
        haversineMeters(
          { lng: coords[i - 1]![0], lat: coords[i - 1]![1] },
          { lng: coords[i]![0], lat: coords[i]![1] },
        ),
    );
  }
  const total = cum[cum.length - 1] || 1;
  return fractions.map((f) => {
    const target = f * total;
    let i = 1;
    while (i < cum.length && cum[i]! < target) i++;
    if (i >= coords.length) {
      const last = coords[coords.length - 1]!;
      return { lng: last[0], lat: last[1] };
    }
    const segLen = cum[i]! - cum[i - 1]! || 1;
    const t = (target - cum[i - 1]!) / segLen;
    const a = coords[i - 1]!;
    const b = coords[i]!;
    return { lng: a[0] + (b[0] - a[0]) * t, lat: a[1] + (b[1] - a[1]) * t };
  });
}

/** 2経路が幾何的にほぼ同一か (中間の弧長サンプルが全て近ければ重複扱い)。 */
/** 経路の弧長 (m)。 */
function routeArcLengthM(pts: Array<[number, number]>): number {
  let sum = 0;
  for (let i = 1; i < pts.length; i++) {
    sum += haversineMeters(
      { lng: pts[i - 1]![0], lat: pts[i - 1]![1] },
      { lng: pts[i]![0], lat: pts[i]![1] },
    );
  }
  return sum;
}

function routesSimilar(
  a: Array<[number, number]>,
  b: Array<[number, number]>,
): boolean {
  const fr = [0.2, 0.4, 0.6, 0.8];
  const sa = sampleRouteAlong(a, fr);
  const sb = sampleRouteAlong(b, fr);
  // 許容差は経路長に比例させる (上限は従来の 35m)。固定 35m だと短い経路
  // (~130m の学校ルート等) で「別の面から出る別の道」まで同一扱いになり、
  // 直進経路の候補が消える (実測: 阪本小学校の西面直進ルート)。
  const tol = Math.min(
    CANDIDATE_DEDUP_M,
    Math.max(12, 0.15 * Math.min(routeArcLengthM(a), routeArcLengthM(b))),
  );
  return sa.every((p, i) => haversineMeters(p, sb[i]!) < tol);
}

/**
 * through-route 用の waypoint 配列を組み立てる。
 * 橋 via とユーザー経由点を統合し start→end 進行順へ整列、両端に s,e を付ける。
 * userVias 空・bridge 無し → [s,e](従来の直行)、userVias 空・bridge あり →
 * [s,bridge,e](従来の橋経由)と等価なので、既存挙動を壊さない。
 */
export function buildComboWaypoints(
  s: LngLat,
  e: LngLat,
  viaBridge: LngLat | null,
  userVias: LngLat[],
): LngLat[] {
  const mids = orderByProgress(
    [...(viaBridge ? [viaBridge] : []), ...userVias],
    s,
    e,
  );
  return [s, ...mids, e];
}

/**
 * 経路候補(複数)を取得。始終点は入口起点/入口終点で解決(候補間で共通,1回)。
 *
 * 徒歩プロファイルの Mapbox alternatives はほぼ1本しか返さないため、候補を合成する:
 *   1. 直進(alternatives) を取得
 *   2. start→end 直線に垂直な**経由地を左右に挟んだ迂回ルート**
 *   3. 遠回りな経路では近隣の**橋を経由した橋認識ルート** (川越え等でMapbox徒歩が最短でない
 *      橋を選ぶのを補正。橋経由の方が短くなるケースを拾う) — bridgeViaRoutes
 *   4. 各ルートを後処理 (境界切詰→直線化→内部延長)
 *   5. 最短からの倍率でとんでもない遠回りを除外
 *   6. 幾何的に同一な候補を重複排除し、距離昇順で最大 MAX_ROUTE_CANDIDATES 本
 * 少なくとも1本(直進)は必ず返す。全滅時のみ throw。
 */
export async function fetchAccurateRouteCandidates(
  property: LngLat,
  destCoords: LngLat,
  polygon?: DestinationV2['polygon'],
  footprint?: DestinationV2['polygon'],
  waypoints: LngLat[] = [],
): Promise<AccurateRouteResult[]> {
  const ring = destinationRing(property, destCoords, polygon);
  // 避難先の内側に置いた最後の経由点は入口 = 終点 (fetchAccurateRoute と同じ)。
  const { entrance, vias: viaWaypoints } = splitEntranceWaypoint(waypoints, ring);
  const destHoles = ring ? holesWithin(ring, polygon?.holes) : [];
  const hasUserVias = viaWaypoints.length > 0;
  const resolved = await resolveRouteEndpoints(
    property,
    destCoords,
    ring,
    footprint,
    true,
    entrance,
  );
  const { starts, fpRing, destEntries } = resolved;
  let endpoint = resolved.endpoint;
  // 経路回廊の車道中心線 (歩道スナップ先)。1回だけ取得し全候補の後処理で共有。
  // Directions 群と並行して読める (プローブ chain 上は他のプローブと直列)。
  const roadLinesPromise = fetchRoadCenterlinesWithRetry([
    property,
    ...starts,
    endpoint,
    ...destEntries,
  ]);

  // 道路面ごとに直進 (alternatives) を取得し、暫定の最短面 (p0) を得る。
  // ここでの最短は橋補正前なので primary 面の確定には使わない (橋ゲート判定用)。
  const routesPerStart = () =>
    Promise.all(
      starts.map((s) =>
        fetchWalkingRoutes(s, endpoint).catch(() => [] as WalkingRouteResult[]),
      ),
    );
  let perStart = await routesPerStart();
  // 入口の経由点は、入口に一番近い道から入る (fetchAccurateRoute と同じ)。
  const firstRoute = perStart.flat()[0];
  const gate =
    entrance && ring && firstRoute
      ? entranceGate(firstRoute.coordinates, ring, entrance, destEntries)
      : null;
  if (gate) {
    endpoint = gate;
    perStart = await routesPerStart();
  }
  let p0 = 0;
  let naturalShortest = Infinity;
  perStart.forEach((rs, i) => {
    const d = rs.length ? Math.min(...rs.map((r) => r.distance)) : Infinity;
    if (d < naturalShortest) {
      naturalShortest = d;
      p0 = i;
    }
  });
  const start0 = starts[p0] ?? property;

  // 橋認識: 「最短の橋」(経由で自然経路より短くなる橋) を特定する。
  // Mapbox の徒歩探索は川を渡る際に最適でない橋を選ぶ癖があるため、以降の
  // バリエーションは全てこの橋を経由させる。別の橋を渡る候補は bridge フィルタで落ちる。
  const { routes: bridgeRoutes, crossing: viaBridge } =
    naturalShortest < Infinity
      ? await resolveBridgeRoutes(start0, endpoint, naturalShortest)
      : { routes: [] as WalkingRouteResult[], crossing: null as LngLat | null };

  // primary 面 (経路の始点) は **橋補正後の経路が一番近くなる道路面** で確定する。
  // 自然経路で選ぶと、正しい橋を渡らない面が長く見えて誤った面が primary になる
  // (実測: プライムアーバンで南側 path 面が primary になり、北面 635m が候補側に落ちた)。
  // 各面の橋補正経路は「経路が遠くなる方の道路面」の候補としてもそのまま提示する。
  const faceRoutes: Array<{ route: WalkingRouteResult; idx: number }> = [];
  let primaryIdx = p0;
  if (starts.length > 1) {
    const faceSettled = await Promise.allSettled(
      starts.map((s) =>
        viaBridge
          ? fetchWalkingRouteThrough([s, viaBridge, endpoint])
          : fetchWalkingRoute(s, endpoint),
      ),
    );
    let bestD = Infinity;
    faceSettled.forEach((r, i) => {
      if (r.status !== 'fulfilled') return;
      faceRoutes.push({ route: r.value, idx: i });
      // 面の採用も「距離 + 折れペナルティ」— 数m短いだけの折れ経路より直進を選ぶ。
      // 折れ角は平滑化後の形で測る (生ジオメトリの歩道ノイズで順位が壊れるため)。
      const score =
        r.value.distance +
        smoothedTurnDeg(r.value.coordinates) * TURN_PENALTY_M_PER_DEG;
      if (score < bestD) {
        bestD = score;
        primaryIdx = i;
      }
    });
  }
  const start = starts[primaryIdx] ?? property;
  const natural = perStart[primaryIdx] ?? [];

  // 組み合わせ候補: 物件側の各道路面 (A,B,C… A=primary) × 避難先側の各進入点
  // (1,2,… 1=最寄り) の **全ペア** で経路を作る (ユーザー要望: A1〜C6 のように選べる)。
  // どの面・どの入口が正しいかはユーザーにしか特定できないため全組を提示し、
  // 迂回率の傾斜も付けない (entry)。実在の進入点が無い避難先は境界ウォークで代替。
  // 隣接入口の組は経路がほぼ同一になり幾何デデュープで畳まれる。
  const isRealEntries = destEntries.length > 1;
  // endpoint が OSM 入口の場合は destEntries と別点なので、slice でなく近接除外で
  // 重複だけを落とす (先頭の実入口を取りこぼさない)。
  // 入口をユーザーが指定したときは、入口のバリエーションは作らない (面だけ変える)。
  const entryList: LngLat[] = entrance
    ? [endpoint]
    : isRealEntries
    ? [
        endpoint,
        ...destEntries
          .filter((e) => haversineMeters(e, endpoint) > 20)
          .slice(0, MAX_ENTRY_VARIANTS),
      ]
    : [endpoint, ...(ring ? boundaryPointsAround(ring, endpoint) : [])];
  const faceList: LngLat[] = [
    starts[primaryIdx] ?? property,
    ...starts.filter((_, i) => i !== primaryIdx),
  ];
  const FACE_LETTERS = 'ABCDEFGH';
  const combos: Array<{ s: LngLat; e: LngLat; label: string }> = [];
  faceList.forEach((s, fi) => {
    entryList.forEach((e, ei) => {
      combos.push({ s, e, label: `${FACE_LETTERS[fi] ?? '?'}${ei + 1}` });
    });
  });
  // 全組み合わせは数十件になるため並列数を制限して取得 (レート制限による歯抜け防止)。
  // 最短橋があればどの組もそこを経由する (どの面・入口でも正しい橋を渡る)。
  const comboResults = await mapWithConcurrency(
    combos,
    COMBO_CONCURRENCY,
    ({ s, e }) => {
      const wp = buildComboWaypoints(s, e, viaBridge, viaWaypoints);
      return wp.length > 2
        ? fetchWalkingRouteThrough(wp)
        : fetchWalkingRoute(s, e);
    },
  );

  // 経由地(垂直オフセット) は primary ペアで従来通り (回廊の多様性)。
  // ただしユーザー経由点があるときは明示誘導なので合成 via は生成しない。
  const viaPoints = hasUserVias
    ? []
    : perpendicularViaPoints(start, endpoint);
  const perpSettled = await Promise.allSettled(
    viaPoints.map((via) => fetchWalkingRouteThrough([start, via, endpoint])),
  );

  // entry=true は「ユーザーが出入り口を選ぶための候補」(面×入口の組) — 迂回率対象外。
  // 組み合わせを先頭に置く (同一経路の dedupe でラベル付きが生き残るように)。
  const raw: Array<{
    route: WalkingRouteResult;
    entry: boolean;
    label?: string;
  }> = [];
  comboResults.forEach((r, i) => {
    if (r) {
      raw.push({ route: r, entry: isRealEntries, label: combos[i]!.label });
    }
  });
  // ユーザー経由点があるときは comboResults のみが経由点を通過している。
  // 経由点を通らない自然経路・垂直via・橋単独候補は「全候補が経由点を通過」の
  // 不変条件を破るため追加しない。
  if (!hasUserVias) {
    raw.push(...natural.map((r) => ({ route: r, entry: false })));
    for (const s of perpSettled) {
      if (s.status === 'fulfilled') raw.push({ route: s.value, entry: false });
    }
    raw.push(...bridgeRoutes.map((r) => ({ route: r, entry: false })));
  }
  const roadLines = (await roadLinesPromise) ?? [];
  if (raw.length === 0) {
    // 直進すら取れない = Directions障害。単発の実経路取得へフォールバック。
    const route = await fetchWalkingRoute(start, endpoint);
    return [
      {
        coordinates: postprocessRoute(route.coordinates, ring, fpRing, {
          roadLines,
          destHoles,
          entrance,
        }),
        distance: Math.round(route.distance),
      },
    ];
  }

  const processed = raw
    .map(({ route, entry, label }) => {
      // 進入点候補は終点側トリムをスキップ (境界沿いの街路が polygon を掠めると
      // 「最初に触れた点」で切られ、遠い入口への経路が全て同一形状に潰れるため)。
      const coordinates = postprocessRoute(route.coordinates, ring, fpRing, {
        trimDest: !entry,
        roadLines,
        destHoles,
        entrance,
      });
      const offRoadRun = maxOffRoadRunM(coordinates, roadLines, ring, fpRing);
      return {
        coordinates,
        distance: Math.round(route.distance),
        entry,
        label,
        offRoadRun,
        // 順位は距離 + 折れペナルティ。距離が拮抗する候補は「まっすぐな経路」を
        // 上位/既定にする (実測: 北面発の71°折れ経路 127m が西面発の直進経路より
        // 数m短いだけで先頭になり、ユーザーには不自然に見えた)。90°折れ ≈ +22.5m。
        // + 道路外ランペナルティ: 見える道路の無い回廊 (歩行者デッキ等) を
        //   長く走る候補は、多少長くても街路経由の候補より下位にする。
        rank:
          route.distance +
          routeTotalTurnDeg(coordinates) * TURN_PENALTY_M_PER_DEG +
          Math.max(0, offRoadRun - OFF_ROAD_RUN_ALLOWANCE_M) *
            OFF_ROAD_RUN_PENALTY_PER_M,
      };
    })
    .sort((a, b) => a.rank - b.rank);

  // 道路外ランが極端な候補 (歩行者デッキ回廊など) は、クリーンな候補が十分ある
  // 限り一覧から除外する。順位の降格だけだと候補プレビューに残り「道路外を直進
  // する破線」として見えてしまう (実測: 横浜ランドマークタワー — デッキ経由 121m)。
  const cleanish = processed.filter(
    (c) => c.offRoadRun <= OFF_ROAD_RUN_DROP_M,
  );
  const roadworthy = cleanish.length >= 3 ? cleanish : processed;

  // 別の橋を渡る候補は出さない: 最短橋が特定できているときは、その横断点を通らない候補
  // (自然経路・垂直オフセットが劣った橋を渡ったもの・他の橋の経由候補) を除外する。
  // 候補の多様性は「どの橋か」でなく「どの道・どの入口へ着くか」で出す。
  // 万一全滅した場合は従来どおり全候補から選ぶ (安全網)。
  const viaBest = viaBridge
    ? roadworthy.filter(
        (c) =>
          minDistanceToRouteMeters(c.coordinates, viaBridge) <= BRIDGE_MATCH_M,
      )
    : roadworthy;
  const pool = viaBest.length > 0 ? viaBest : roadworthy;

  // 迂回率の基準は「実距離の最短」(rank 順の先頭は折れペナルティ込みなので使わない)
  const shortest = Math.min(...pool.map((p) => p.distance));
  const kept: AccurateRouteResult[] = [];
  for (const cand of pool) {
    // 最短に対して極端に長い迂回は除外 (歩道が無く大回りした候補等)。
    // 進入点候補 (entry) には適用しない — 裏側の入口が本当の入口かもしれず、
    // 入口の位置 (物件からの遠近) で候補に傾斜を付けない (ユーザー方針)。
    if (!cand.entry && cand.distance > shortest * CANDIDATE_MAX_DETOUR_RATIO) {
      continue;
    }
    // 既存候補と幾何的に同一なら重複排除。ラベル無しの採用済み候補と同一経路の
    // 組み合わせ候補が来た場合は、組ラベルだけ継承する (どの面×入口の経路かを示す)。
    const dup = kept.find((k) => routesSimilar(k.coordinates, cand.coordinates));
    if (dup) {
      if (!dup.label && cand.label) dup.label = cand.label;
      continue;
    }
    kept.push({
      coordinates: cand.coordinates,
      distance: cand.distance,
      ...(cand.label ? { label: cand.label } : {}),
    });
    if (kept.length >= MAX_ROUTE_CANDIDATES) break;
  }
  return kept.length > 0
    ? kept
    : [{ coordinates: pool[0]!.coordinates, distance: pool[0]!.distance }];
}

/**
 * 形状(公式ポリゴン/OSM)が取得できない避難先向けの簡易円ポリゴン。
 * 座標中心・半径 radiusMeters の近似円 (segments 角形) を返す。
 * 範囲を常に「新しい避難先の位置」へ更新するためのフォールバック表示に使う。
 */
export function fallbackRangeRing(
  center: LngLat,
  radiusMeters = 50,
  segments = 32,
): Array<[number, number]> {
  const dLat = radiusMeters / 111320;
  const dLng =
    radiusMeters / (111320 * Math.cos((center.lat * Math.PI) / 180) || 1);
  const ring: Array<[number, number]> = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    ring.push([
      center.lng + dLng * Math.cos(a),
      center.lat + dLat * Math.sin(a),
    ]);
  }
  return ring;
}

/** 直線フォールバック (境界最近接点まで)。Directions が使えない時用。 */
export function straightFallbackRoute(
  property: LngLat,
  destCoords: LngLat,
  polygon?: DestinationV2['polygon'],
): AccurateRouteResult {
  const ring = destinationRing(property, destCoords, polygon);
  const end = ring
    ? (nearestPointOnPolygonBoundary(ring, property) ?? destCoords)
    : destCoords;
  let coordinates: Array<[number, number]> = [
    [property.lng, property.lat],
    [end.lng, end.lat],
  ];
  if (ring) {
    coordinates = extendRouteIntoPolygon(coordinates, ring, DESTINATION_PENETRATION_M);
    coordinates = trimRouteAtHoles(coordinates, holesWithin(ring, polygon?.holes));
  }
  return {
    coordinates,
    distance: Math.round(haversineMeters(property, end)),
  };
}

/**
 * 与えた点群が地図領域に収まる center/zoom を Web Mercator で概算する。
 * bearing (地図の向き) を渡すと、その向きの画面で範囲を測る (向きは保つ)。
 */
export function fitMapState(
  points: LngLat[],
  widthPx = ES_LAYOUT.mapWidth,
  heightPx = ES_LAYOUT.bodyHeight,
  padding = 0.22,
  bearing = 0,
): MapState {
  if (points.length === 0) return { center: [NaN, NaN], zoom: 15, bearing, pitch: 0 };
  const e = screenExtent(points, bearing);
  const TILE = 512;
  const fracW = Math.max(e.maxX - e.minX, 1e-7);
  const fracH = Math.max(e.maxY - e.minY, 1e-7);
  const zoomW = Math.log2((widthPx * (1 - padding)) / (TILE * fracW));
  const zoomH = Math.log2((heightPx * (1 - padding)) / (TILE * fracH));
  let zoom = Math.min(zoomW, zoomH);
  if (!Number.isFinite(zoom)) zoom = 15;
  zoom = Math.max(12, Math.min(17.5, zoom));
  const c = screenPointToLngLat((e.minX + e.maxX) / 2, (e.minY + e.maxY) / 2, bearing);
  return { center: [c.lng, c.lat], zoom, bearing, pitch: 0 };
}

/**
 * 公式名から OSM 形状検索用の名称バリアントを生成する。
 * 公式データの名称は OSM と表記が揺れるため、以下を候補に加える:
 *   - 括弧書き除去: 「本願寺津村別院（北御堂）」→「本願寺津村別院」
 *   - 括弧内の通称:  同上 →「北御堂」、「開平小学校（運動場）」→ (運動場は短すぎ除外)
 *   - 設置主体付き:  学校等は「◯◯区立△△小学校」で登録されがち
 * (スペース揺れは overpass 側の \s* 許容マッチで吸収)
 */
export function landmarkNameVariants(
  name: string,
  municipality?: string,
): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    const t = s.trim();
    if (t.length >= 2 && !out.includes(t)) out.push(t);
  };
  push(name);
  const base = name.replace(/[（(][^（()）]*[）)]/g, '');
  push(base);
  // 「〜一帯/地区/地域/周辺/一円/付近」等の範囲呼称は OSM だと素の施設名で
  // 登録されがち (例「大阪城公園一帯」→ OSM「大阪城公園」)。素の名前も候補に。
  const areaStripped = base.replace(/(一帯|地区|地域|周辺|一円|付近)$/, '');
  push(areaStripped);
  for (const m of name.matchAll(/[（(]([^（()）]+)[）)]/g)) {
    // 「運動場」「校庭」等の場所種別は施設名ではないので除外
    if (!/^(運動場|校庭|グラウンド|体育館|講堂)$/.test(m[1]!)) push(m[1]!);
  }
  if (municipality) push(`${municipality}立${base}`);
  return out;
}

/** マスターの polygon を DestinationV2 の形 (ring 配列) へ複製する。 */
function copyPolygon(
  place: EvacuationPlaceExtended,
): DestinationV2['polygon'] | undefined {
  const copy = (ring: Array<[number, number]>) =>
    ring.map(([lng, lat]) => [lng, lat] as [number, number]);
  const holes = place.polygon?.holes;
  return place.polygon
    ? {
        coordinates: place.polygon.coordinates.map(copy),
        // 除外区域 (都の広域避難場所の内側ring) も写す。無ければ省く。
        ...(holes && holes.length > 0 ? { holes: holes.map(copy) } : {}),
      }
    : undefined;
}

/**
 * マスターが持つ「暫定の簡易円」(approxShape)。実形状ではないので通常は使わず、
 * OSM 取得も失敗した時の最終手段としてだけ finalizeDestination に渡す。
 */
function approxPolygonOf(
  place: EvacuationPlaceExtended,
): DestinationV2['polygon'] | undefined {
  return place.approxShape ? copyPolygon(place) : undefined;
}

/**
 * 物件自身の建物フットプリント (青で塗る範囲) を OSM から取得する。
 *
 * 取得できない場合は undefined を返し、MapCanvasV2 が青丸で代替する。
 * 'building-only' は必須 — 既定の 'grounds-first' は「内包する最大の敷地」を
 * 返すため、公園や学校の敷地内に落ちた物件で敷地全体を塗ってしまう
 * (例: おりづるタワーの半径60m内には広島平和記念公園が入る)。
 * 失敗は非致命 (塗りが無いだけで下書きは成立する)。
 */
export async function fetchPropertyFootprint(
  coords: LngLat,
): Promise<PropertyV2['footprint'] | undefined> {
  // まず安定した Mapbox タイルの建物を試す。物件形状が公開 Overpass の間欠的な
  // 504/タイムアウトで取得できず「リロードするまで塗りが付かない」不具合の対処。
  // Mapbox はレート制限が無く、アプリが表示に使うタイルをそのまま読むだけで速い。
  try {
    const mb = await fetchMapboxBuilding(coords);
    if (mb && mb.length >= 3) return { coordinates: [mb] };
  } catch {
    // Mapbox 側の失敗 (プローブ生成不可・非ブラウザ等) は握りつぶし Overpass へ。
  }
  // Mapbox に建物が無い (小さい/新しい建物はタイルに載らないことがある) 時だけ
  // 従来の OSM Overpass にフォールバックする。
  try {
    const ring = await fetchFacilityFootprint(
      coords,
      PROPERTY_FOOTPRINT_RADIUS_M,
      'building-only',
    );
    return ring && ring.length >= 3 ? { coordinates: [ring] } : undefined;
  } catch {
    return undefined;
  }
}

/** 公式マスターの place から DestinationV2 (経路以外) を組み立てる。 */
function placeToDestination(place: EvacuationPlaceExtended): DestinationV2 {
  const coords = placeCoord(place)!;
  const cardLabel = resolveCardLabel(place);
  const landmarkNames =
    place.landmarkNames && place.landmarkNames.length > 0
      ? place.landmarkNames
      : landmarkNameVariants(place.name, place.municipality);
  return {
    name: { ja: cleanPlaceLabel(place.name), en: '' },
    category: place.category,
    distance: 0,
    coords,
    landmarkNames,
    // 実形状のみ採用する。approxShape (暫定円) をここで入れると
    // finalizeDestination の「形状が無ければ OSM 取得」が発火せず、
    // 避難先が永久に円のままになる (円は approxPolygonOf 経由で最終手段に回す)。
    polygon: place.approxShape ? undefined : copyPolygon(place),
    // 自治体が別の呼び名で案内している場所だけ区分見出しを差し替える
    // (例: 北の丸公園 = 千代田区の「災害時退避場所」)。
    ...(cardLabel ? { cardLabel } : {}),
  };
}

/** 公式マスターと地区割当を読み、避難先 2 件を選ぶ (選定そのものは pick-destinations.ts)。 */
export async function pickOfficialDestinations(
  propertyCoords: LngLat,
  address: string,
  opts: PickOptions,
): Promise<PickResult> {
  const [places, assignmentData] = await Promise.all([
    loadEvacuationPlaces(),
    loadAssignmentData(),
  ]);
  const assignment = lookupAssignment(assignmentData, propertyCoords, address);
  return selectOfficialDestinations(places, propertyCoords, opts, assignment);
}

/**
 * 想定最大浸水深をベストエフォートで取得する。
 *
 * 避難所カードの浸水深は「取れたら出す」補助情報なので、API 障害でマップ作成
 * 全体を失敗させない (浸水想定区域外の null と同じ「未設定」に倒す)。
 */
async function safeMaxFloodDepth(coords: LngLat): Promise<string | undefined> {
  try {
    return (await fetchMaxFloodDepth(coords))?.depthLabel;
  } catch {
    return undefined;
  }
}

/**
 * 経路・(必要なら)OSM形状を避難先に反映する。
 *
 * 形状の優先順は 実形状 > OSM実行時取得 > 暫定円 (approxPolygon)。
 * @param approxPolygon マスターの簡易円。OSM でも形状が取れなかった時だけ使う。
 */
async function finalizeDestination(
  propertyCoords: LngLat,
  dest: DestinationV2,
  approxPolygon?: DestinationV2['polygon'],
  footprint?: DestinationV2['polygon'],
  report?: { straight: boolean },
): Promise<DestinationV2> {
  // 形状が無い場合 (点データ / 暫定円しか無い避難先) はベストエフォートで実形状を取得。
  // resolveDestinationPolygon 経由なので Mapbox 建物 → OSM 名称 → OSM 座標内包 の
  // 全段が効く (以前は名称 union だけで、Mapbox・座標内包の恩恵が無かった)。
  if (!dest.polygon) {
    const ring = await resolveDestinationPolygon(
      dest.coords,
      dest.landmarkNames ?? [dest.name.ja],
    );
    if (ring && ring.length >= 3) dest.polygon = { coordinates: [ring] };
  }
  // OSM でも取れなければマスターの簡易円で範囲を示す (形状なしよりは良い)
  let usingApproxCircle = false;
  if (!dest.polygon && approxPolygon) {
    dest.polygon = approxPolygon;
    usingApproxCircle = true;
  }
  /*
   * 実形状と公式の代表点の食い違いを解く。矢印・吹き出し線は代表点を指すので、
   * 描かれている範囲から外れていると「あらぬ方向」を向く。
   *
   * 1) 1区域の形状で、代表点が**大きく**外れている → 形状のほうを捨てる。
   *    実測 (文化総合センター大和田): 代表点は住所解決から7m・OSMの施設ノードから
   *    7m に対し、拾われたポリゴンは83m離れた別の建物だった。マスターの代表点は
   *    公式データで信頼できるので、疑うべきは形状の側。
   *    ただし代表点が敷地の縁のすぐ外にあるのは普通なので、しきい値を置く
   *    (門・入口が代表点になっている等)。
   * 2) 複数区域の形状 → 捨てずに、物件に**近い区域へ代表点を寄せる**。
   *    実測 (中目黒公園一帯): 近い区域681m・遠い区域1147m で代表点は遠いほう。
   *    「一帯」「地区」はどの区域へ逃げてもよい指定なので、代表点の移動が正しい。
   *    複数区域は公式の区域データにしか無い (実行時に取る形状は常に1区域) ので、
   *    1) の「別の建物を拾った」には当たらない。代表点がどの区域の外でも寄せる
   *    (実測: 駒場地区は4区域のどれからも164m外で、1) で範囲ごと消えていた)。
   */
  if (!usingApproxCircle && dest.polygon) {
    const rings = dest.polygon.coordinates ?? [];
    if (rings.length > 1) {
      const nearest = nearestRing(propertyCoords, dest.polygon);
      const c = nearest ? polygonCentroid(nearest) : null;
      if (c) dest.coords = c;
    } else if (rings[0] && !pointInsideRing(dest.coords, rings[0])) {
      const q = nearestPointOnPolygonBoundary(rings[0], dest.coords);
      const gap = q ? haversineMeters(dest.coords, q) : Infinity;
      if (gap > DESTINATION_SHAPE_MAX_GAP_M) {
        dest.polygon = undefined; // 別の建物を拾った疑い — 形状なしで進む
      }
    }
  }
  let route: AccurateRouteResult;
  try {
    /*
     * 経路の取得条件は**編集ページと同一**にする (既定 = 橋認識も入口検索も行う)。
     *
     * 以前は作成時だけ待ち時間を削るため橋認識を切っていた。その結果、作成された
     * 経路と編集ページで取り直した経路が食い違い、物件を開くたびに経路を取り直す
     * 運用になっていた。作成が数秒遅くなっても、出来上がりが編集時と一致するほうが
     * 実務では速い。
     */
    route = await fetchAccurateRoute(
      propertyCoords,
      dest.coords,
      dest.polygon,
      footprint,
      {},
    );
  } catch {
    route = straightFallbackRoute(propertyCoords, dest.coords, dest.polygon);
    if (report) report.straight = true;
  }
  dest.routePolyline = { coordinates: route.coordinates };
  dest.distance = route.distance;
  return dest;
}

/**
 * 1物件分の自動下書きを生成する (入力は物件名・住所等のみ)。
 * 避難先は公式マスターから自動選定し、経路は実徒歩経路 + 境界切り詰め。
 * 物件住所のジオコーディング失敗・近隣マスター無しは例外を投げる。
 */
export async function autoDraftFromOfficial(
  input: OfficialDraftInput,
): Promise<DraftPropertyV2> {
  return (await autoDraftDetailed(input)).draft;
}

/** 下書きと、その作り方の詳細 (取込の要確認一覧の材料)。 */
export interface DetailedDraft {
  draft: DraftPropertyV2;
  detail: PickDetail;
  /** 地図検索が返した住所 (draft.address と同じ) */
  geocodedAddress: string;
  /** 経路が取れず直線にした */
  straightRoute: { evacuationArea: boolean; shelter: boolean };
}

/** autoDraftFromOfficial と同じ下書きを、選定の経緯・直線経路の有無と一緒に返す。 */
export async function autoDraftDetailed(
  input: OfficialDraftInput,
): Promise<DetailedDraft> {
  // 呼び出し側が geocode 済みならその座標を使う (PropertyNewV2 は Step1 で解決済み)
  const propGeo = input.coords
    ? { coords: input.coords, formattedAddress: input.address }
    : await geocodeAddress(input.address);
  const coords = propGeo.coords;

  const addr = input.address || propGeo.formattedAddress;
  const picked = await pickOfficialDestinations(coords, addr, {
    evacuationName: input.evacuationName,
    shelterName: input.shelterName,
    prefecture: prefectureOfAddress(addr),
    municipality: municipalityOfAddress(addr),
  });
  // 物件フットプリントを先に取得する (Mapbox タイルの建物 = 数百ms と速い)。経路の
  // 始点解決に使い、経路が物件の塗りを貫通せず建物の目的地側の縁から発つようにする。
  const footprint = await fetchPropertyFootprint(coords);
  // 2避難先は互いに独立 (共有stateなし) なので並列化する。直列だと待ち時間が積算する。
  const evacReport = { straight: false };
  const shelterReport = { straight: false };
  const [evacuationArea, shelter] = await Promise.all([
    finalizeDestination(
      coords,
      placeToDestination(picked.evacuationArea),
      approxPolygonOf(picked.evacuationArea),
      footprint,
      evacReport,
    ),
    finalizeDestination(
      coords,
      placeToDestination(picked.shelter),
      approxPolygonOf(picked.shelter),
      footprint,
      shelterReport,
    ),
  ]);
  // 選んだ根拠 (割当・名前指定・自動選定の別) を残す。出典は auto のまま —
  // municipal は人が行政に確かめたときだけ (R4 の保護を機械の判定に広げない)。
  evacuationArea.sourceNote = picked.evacuationNote;
  shelter.sourceNote = picked.shelterNote;
  // 浸水深: 明示指定 > 公式データからの自動取得。
  // これまで自動取得はエディタで避難所を選び直したときだけ走っており、
  // 新規作成 (単体・一括) では常に空だった。避難所が決まった時点で取りにいく。
  shelter.floodDepth = input.floodDepth ?? (await safeMaxFloodDepth(shelter.coords));

  // 地図に収める点群: 物件 + 2避難先 + 各経路の全点
  const points: LngLat[] = [coords, evacuationArea.coords, shelter.coords];
  for (const d of [evacuationArea, shelter]) {
    for (const c of d.routePolyline?.coordinates ?? []) {
      points.push({ lng: c[0], lat: c[1] });
    }
  }
  const mapState = fitMapState(points);

  const draft: DraftPropertyV2 = {
    buildingName: { ja: input.buildingName.ja, en: input.buildingName.en ?? '' },
    address: propGeo.formattedAddress,
    coords,
    // QR は自治体の防災ポータルへ (明示指定があればそちらを優先)。
    qrUrl: input.qrUrl || resolveBousaiPortalUrl(input.address),
    destinations: { evacuationArea, shelter },
    mapState,
    labels: [],
    ...(input.propertyType ? { propertyType: input.propertyType } : {}),
    ...(input.hubspotPropertyId
      ? { hubspotPropertyId: input.hubspotPropertyId }
      : {}),
    // 取れなければキー自体を出さない (Firestore は undefined を弾くため)
    ...(footprint ? { footprint } : {}),
  };
  return {
    draft,
    detail: picked.detail,
    geocodedAddress: propGeo.formattedAddress,
    straightRoute: { evacuationArea: evacReport.straight, shelter: shelterReport.straight },
  };
}
